import { FC } from 'react'
import { ButtonsBlock } from '../../../../Edit/components'
import { Modal, CenteredSpin } from '../../../'
import { Alert, Typography } from 'antd'
import style from './style.m.less'
import { useNavigate, Link } from 'react-router-dom'
import { deleteCertificate } from './config'
import {isExpired, signBody} from '../../../../helpers'
import useRequest from '../../../../hooks/useRequest'
import { useAppSelector } from '../../../../../store/hooks'
import { useCurrentUser } from '../../../../hooks/useCurrentUser'
import { useGetTariffRate } from '../../../../hooks'
import { useTranslation } from 'react-i18next'
import {EUserType} from "../../../../../constants/approval";

interface IDeleteModal {
    isModalVisible: boolean
    setIsModalVisible: (value: boolean) => void
    certificateId?: string
    certificateNumber?: string
    capacity: number
}

const DeleteModal: FC<IDeleteModal> = ({
    isModalVisible,
    setIsModalVisible,
    certificateId,
    certificateNumber,
    capacity,
}) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { isUserAdmin, isGOOwner } = useCurrentUser()
    const { fetch: fetchDelete } = useRequest(deleteCertificate)

    const handleCancel = () => {
        setIsModalVisible(false)
    }

    const userInfo = useAppSelector((store) => store.user)
    const { data } = userInfo
    const isSignExpired = isGOOwner && isExpired(data?.certificate_data.token_expiration)
    // У пользователя должна быть МЧД
    const needAttorney = data?.user_type === EUserType.UrLico && isGOOwner && !data?.without_attorney
    // У пользователя должна быть МЧД, но её нет
    const isEmptyAttorney = needAttorney && !data?.machine_readable_attorney
    // У пользователя есть МЧД и у неё истёк срок
    const isAttorneyExpired = needAttorney
        && !!data?.machine_readable_attorney
        && isExpired(data?.machine_readable_attorney.validity_period, true)

    const handleConfirm = async () => {
        const body = { certificate_id: certificateId }
        if (isUserAdmin) {
            fetchDelete({
                body,
                successMessage: {
                    title: 'Успешно',
                    description: 'Сертификат успешно удален',
                },
                onSuccess: () => navigate('/green-tools'),
                errorCodeMessage: true,
            })
        } else {
            const { request_id, signature } = await signBody(body, data)
            fetchDelete({
                body,
                config: {
                    headers: {
                        Signature: signature,
                    },
                },
                getParams: {
                    request_id,
                },
                successMessage: {
                    title: 'Успешно',
                    description: 'Сертификат успешно удален',
                },
                onSuccess: () => navigate('/user-my-green-tools'),
                errorCodeMessage: true,
            })
        }
    }

    const { loadingTariffs, balance, rate } = useGetTariffRate({
        dependency: isModalVisible && !isUserAdmin,
        id: 19,
        volume: capacity,
    })

    if (loadingTariffs) {
        return <CenteredSpin />
    }

    return (
        <Modal isModalVisible={isModalVisible}>
            <div className={style.mainWrapper}>
                <div className={style.contentWrapper}>
                    <div className={style.modalHeader}>{'Удалить сертификат происхождения'}</div>
                    <div
                        className={style.modalText}
                    >{` Вы действительно хотите удалить сертификат происхождения №${certificateNumber}? Отменить это действие будет невозможно.`}</div>
                    {!isUserAdmin && (
                        <Alert
                            className={style.modalAlert}
                            message={
                                <div className={style.modalAlertText}>
                                    <div>
                                        За операцию взымается оплата в размере{' '}
                                        <Typography.Text strong>{rate}</Typography.Text>
                                    </div>
                                    <div>Баланс платежного счета: {balance}</div>
                                </div>
                            }
                            type="warning"
                            showIcon
                        />
                    )}
                    {isSignExpired && (
                        <Alert
                            className={style.signError}
                            message={'Истек срок действия ЭП'}
                            type="error"
                            showIcon
                        />
                    )}
                    {(isEmptyAttorney || isAttorneyExpired) && (
                        <Alert
                            className={style.signError}
                            message={isEmptyAttorney ? t('profile.attorneyIsMissing') : t('profile.attorneyIsExpired')}
                            type="error"
                            showIcon
                        />
                    )}
                    <div className={style.buttonsBlock}>
                        <ButtonsBlock
                            isDelete
                            confirmAction={handleConfirm}
                            cancelAction={handleCancel}
                            confirmTitle={isUserAdmin ? 'Удалить' : 'Подписать и удалить'}
                            confirmDisabled={isSignExpired || isEmptyAttorney || isAttorneyExpired}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    )
}

export default DeleteModal
